import React, { useRef } from 'react';
import { Button, Modal } from 'antd';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import * as Yup from 'yup';

import SubmitButton from 'components/formik/SubmitButton';
import BaseAlert from 'components/wrappers/BaseAlert';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import Checkbox from 'components/formik/Checkbox';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { confirmPricingAgreement } from 'redux/reducers/accountSlice';
import { disablePastDates } from 'utils/dateUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './ConfirmPricingAgreementModal.module.less';

type Props = {
  onAgreementSent: () => void;
  onClose: () => void;
};

interface ConfirmPricingAgreementFormValues {
  confirmationSignature: string | undefined;
  confirmationDate?: Date;
  isConfirmation: boolean;
}

const validationSchema = Yup.object({
  confirmationSignature: Yup.string()
    .required('Field is required')
    .trim()
    .max(250, 'Customer signature should be 250 characters maximum'),
  confirmationDate: Yup.date()
    .required('Field is required')
    .min(new Date(), 'Confirmation date should not be in the past')
    .nullable(),
  isConfirmation: Yup.boolean().oneOf([true], 'Confirmation is required'),
});

export default function ConfirmPricingAgreementModal(props: Props) {
  const { onClose, onAgreementSent } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const initialValues: ConfirmPricingAgreementFormValues = {
    confirmationSignature: '',
    confirmationDate: new Date(),
    isConfirmation: false,
  };

  const handleCancel = () => {
    if (formRef?.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const handleSubmit = async (values: ConfirmPricingAgreementFormValues) => {
    const result = await dispatch(confirmPricingAgreement(values));

    if (confirmPricingAgreement.fulfilled.match(result)) {
      await onAgreementSent();

      BaseAlert.success('Pricing agreement confirmed.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    onClose();
  };

  return (
    <BaseModal
      title="Confirm pricing agreement"
      maxWidth={468}
      open={true}
      closable={true}
      onCancel={handleCancel}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        <Form layout="vertical" className={styles.confirmPricingAgreementForm}>
          <div className="modal-scroll-wrapper">
            <div className="modal-title">Pricing agreement</div>

            <FormItem
              name="confirmationSignature"
              label="Customer signature *"
              data-id="customerSignatureField"
            >
              <Input
                name="confirmationSignature"
                placeholder="Enter your full name"
                autoComplete="off"
              />
            </FormItem>

            <FormItem
              name="confirmationDate"
              label="Date signed *"
              data-id="confirmationDateField"
            >
              <DatePicker
                name="confirmationDate"
                disabled
                disabledDate={disablePastDates}
                placeholder="Select the confirmation date"
                format="MM/DD/YYYY"
                className="full-width"
              />
            </FormItem>

            <FormItem name="isConfirmation">
              <Checkbox name="isConfirmation">
                I agree and confirm that I have reviewed and agreed to these
                pricing terms.
              </Checkbox>
            </FormItem>
          </div>

          <BaseModalFooter>
            <Button onClick={handleCancel}>Cancel</Button>
            <SubmitButton>Save</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
